import {
  LagtTilAvType,
  type Lenke,
  LenkeType,
  type MittVerktoyItem,
  OpprettetAvType,
  StatusType,
  type Verktoy,
  VerktoyKategori,
} from '../types/minhelseentities';
import type { HNVerktoyFrontend } from '../types/Resources';
import { PageRoutes, type SessionStorageKeys } from '../types/types';

import { isMobileUA } from '@helsenorge/designsystem-react';
import { trackTool } from '@helsenorge/framework-utils/adobe-analytics';

export const getVerktoyById = (verktoyList: Verktoy[], verktoyId: string | null): Verktoy | undefined => {
  return verktoyList.find(verktoy => verktoy.VerktoyId === verktoyId);
};

export const isLaeringsaktivitetCreatedByInnbygger = (opprettetAvType: OpprettetAvType | undefined): boolean => {
  return opprettetAvType === OpprettetAvType.ProfilGuid;
};

export const isLaeringsaktivitetCreatedByInnbyggerMineVerktoy = (lagtTilAvType: number | undefined): boolean => {
  return lagtTilAvType === LagtTilAvType.InnbyggerSelv;
};

export function isMobile(): boolean {
  return isMobileUA();
}

export const erApenVerktoy = (kategori: VerktoyKategori): boolean => {
  return (
    kategori === VerktoyKategori.IngenBrukerprofil ||
    kategori === VerktoyKategori.EgenBrukerprofil ||
    kategori === VerktoyKategori.LokalBrukerprofil
  );
};

export const erVerktoyTilgjengelig = (verktoy: Verktoy): boolean => {
  return verktoy.StatusType === StatusType.Publisert && gyldigFraOgTilDato(verktoy.GyldigFraDato, verktoy.GyldigTilDato);
};

export const erVerktoyTilgjengeligMittVerktoyItem = (verktoy: MittVerktoyItem): boolean => {
  return verktoy.ErVerktoyTilgjengelig === true;
};

export const erVerktoyTilgjengeligMineVerktoy = (mittVerktoyItem: MittVerktoyItem): boolean => {
  return !!mittVerktoyItem.ErVerktoyTilgjengelig;
};

export const createHref = (pathname: string): string => {
  const linkEl = document.createElement('a');
  linkEl.href = pathname;
  return linkEl.href;
};

export const idag = (): Date => {
  const nowDate = new Date();
  nowDate.setHours(0, 0, 0, 0);
  return nowDate;
};

const fjernTid = (dato: Date): Date => {
  return new Date(dato.setHours(0, 0, 0, 0));
};

const hasValue = (valueToCheck: string | null | undefined): boolean => {
  if (valueToCheck !== undefined && valueToCheck !== null) {
    return true;
  }
  return false;
};

export const gyldigFraOgTilDato = (fraDatoStr: string | null | undefined, tilDatoStr: string | null | undefined): boolean => {
  if (hasValue(fraDatoStr) === false && hasValue(tilDatoStr) === false) {
    return true;
  }

  const i_dag = idag();
  if (i_dag === undefined) {
    throw new Error('Warning: Klarer ikke lese dagens dato');
  }

  if (hasValue(fraDatoStr) === true && hasValue(tilDatoStr) === false) {
    // Kun fra er satt
    return idag() >= fjernTid(new Date(fraDatoStr as string));
  }
  // Både fra og til er satt
  if (hasValue(fraDatoStr) === true && hasValue(tilDatoStr) === true) {
    const fra = fjernTid(new Date(fraDatoStr as string));
    const til = fjernTid(new Date(tilDatoStr as string));
    return idag() >= fra && idag() <= til;
  }
  // Bare til er satt
  if (hasValue(fraDatoStr) === false && hasValue(tilDatoStr) === true) {
    return idag() <= fjernTid(new Date(tilDatoStr as string));
  }
  return true;
};

export function trackAnalyticsBasertPaaLenkeType(lenkeType: LenkeType, verktoyNavn: string): void {
  switch (lenkeType) {
    case LenkeType.NedlastingAppAndroid:
      trackTool('Verktøy', '', verktoyNavn, 'åpne verktøy i Google Play');
      break;
    case LenkeType.NedlastingAppIphone:
      trackTool('Verktøy', '', verktoyNavn, 'åpne verktøy i App Store');
      break;
    default:
      trackTool('Verktøy', '', verktoyNavn, 'åpne verktøy');
      break;
  }
}

export function scrollBrukerenTilVerktoyet(verktoy: Verktoy): void {
  const guid = verktoy.VerktoyId;
  const findElem = `[data-verktoy-guid="${guid}"]`;
  const elements = document.querySelectorAll(findElem);
  if (elements.length > 0) {
    const node = elements[0] as HTMLElement;
    window.scrollTo(0, node.offsetTop);
  }
}

export function removeTralingAndEmptyParamFromUrl(url: string): string {
  if (url.endsWith('/')) {
    return url.slice(0, -1);
  }
  if (url.endsWith('/?')) {
    return url.slice(0, -2);
  }
  if (url.endsWith('//')) {
    return url.slice(0, -2);
  }
  if (url.endsWith('//?')) {
    return url.slice(0, -3);
  }
  return url;
}

export const getDeleteButtonText = (verktoy: Verktoy, resources?: HNVerktoyFrontend): string | undefined => {
  if (verktoy?.Laeringsressurs && verktoy.Laeringsressurs.KreverForskrivning) {
    return resources?.deleteVerktoyButtonText;
  }
  return resources?.removeVerktoyButtonText;
};

export const getDeleteButtonTextMineVerktoy = (mittVerktoyItem: MittVerktoyItem, resources?: HNVerktoyFrontend): string | undefined => {
  if (mittVerktoyItem.KreverForskrivning) {
    return resources?.deleteVerktoyButtonText;
  }
  return resources?.removeVerktoyButtonText;
};

export const urlExists = (lenker: Lenke[], lenkeType: LenkeType): boolean => {
  if (lenker && lenker.find(lenke => lenke.LenkeType === lenkeType)) {
    return true;
  }
  return false;
};

export const getCurrentPageRoute = (location: string): string => {
  if (location.includes(PageRoutes.finnVerktoy)) {
    return PageRoutes.finnVerktoy;
  } else {
    return PageRoutes.entryPage;
  }
};

export function getInlineCheckboxClass(): string {
  return isMobileUA() === false ? 'verktoy-filter__inline-checkbox_desktop' : 'verktoy-filter__inline-checkbox';
}

export const shouldScrollToTop = (verktoyListe: Verktoy[], pathName: string): boolean => {
  const urlHasAVerktoyId = verktoyListe.some(verktoy => pathName.includes(verktoy.VerktoyId));
  return urlHasAVerktoyId ? false : true;
};

export const scrollAndFocus = (verktoyListe: Verktoy[], listElementRef: React.RefObject<HTMLLIElement | null>): void => {
  if (shouldScrollToTop(verktoyListe, location.pathname)) {
    window.scrollTo(0, 0);
  } else {
    listElementRef?.current?.scrollIntoView();
    listElementRef?.current?.focus();
  }
};

export const removeSessionStorage = (sessionStorageKeys: SessionStorageKeys[]): void => {
  sessionStorageKeys.forEach(sessionStorageKey => {
    sessionStorage.removeItem(sessionStorageKey);
  });
};

export const isGuid = (value: string): boolean => {
  const pattern = /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/gi; // NOSONAR
  return value.match(pattern) !== null;
};

export const erLaeringsaktivitetFn = (laeringsaktiviteter: Verktoy[], verktoyId: string | null): boolean => {
  return laeringsaktiviteter.filter(item => verktoyId === item.VerktoyId).length > 0;
};

export const erLaeringsaktivitetMineVerktoyFn = (mineVerktoy: MittVerktoyItem[], verktoyId: string | null): boolean => {
  return mineVerktoy.filter(item => verktoyId === item.VerktoyId).length > 0;
};
